import { useEffect, useState } from "react";
import { ArrowRight, ArrowUpRight, Check, Hammer, Layers, Sparkles, Award, Building2, Home as HomeIcon, ChefHat, DoorOpen, Sofa, TreePine, Boxes } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import FloatingActions from "../components/FloatingActions";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/344181e7-3672-4829-aec4-8eb5cd6ff707/images/060ee8fa8c65f6fd552fdce4aea91bd733bdc7f7c01b57a1e9cb18fb8fd80bea.png";
const LEGACY_IMG = "https://customer-assets-agu9un31.emergentagent.net/job_web-launch-305/artifacts/f14yj3bq_IMG_5481.webp";
const DOOR_FEATURE = "https://customer-assets-agu9un31.emergentagent.net/job_web-launch-305/artifacts/bggw9g21_ChatGPT%20Image%20May%2022%2C%202026%20at%2002_55_12%20PM.png";
const MATERIALS_HERO = "https://customer-assets-agu9un31.emergentagent.net/job_web-launch-305/artifacts/fjk9icbq_ChatGPT%20Image%20May%2022%2C%202026%20at%2003_01_23%20PM.png";

const SERVICES = [
  { icon: ChefHat, title: "Modular Kitchens", desc: "Smart storage, luxury finishes, intelligent layouts.", img: "https://images.unsplash.com/photo-1663811396777-05505d999151?w=1200&q=85" },
  { icon: Boxes, title: "Wardrobes & Storage", desc: "Walk-ins, sliding wardrobes, backlit interiors.", img: "https://images.unsplash.com/photo-1649361811423-a55616f7ab11?w=1200&q=85" },
  { icon: DoorOpen, title: "Doors & CNC Panels", desc: "Veneer, solid wood, glass, French, tall doors.", img: "https://images.unsplash.com/photo-1559006863-03e0799b836a?w=1200&q=85" },
  { icon: Sofa, title: "Furniture & Turnkey", desc: "End-to-end interior delivery — concept to handover.", img: "https://images.unsplash.com/photo-1742192757416-27d69a5d5029?w=1200&q=85" },
  { icon: TreePine, title: "Timber & Wood Processing", desc: "Teak, Sheesham, Eucalyptus — raw to remarkable.", img: "https://images.unsplash.com/photo-1601058268499-e52658b8bb88?w=1200&q=85" },
];

const DOOR_TYPES = [
  "Engineered Veneer", "Engineered Malaysian Ply", "Glass Doors",
  "Net Doors", "Solid Wood", "Tall Doors (10ft+)", "French Doors",
];

const MATERIALS = [
  { title: "MDF", desc: "Smooth, dense, paint-ready surfaces." },
  { title: "HDHMR", desc: "Moisture-resistant for kitchens & bathrooms." },
  { title: "Plywood", desc: "Marine, BWP, commercial grades." },
  { title: "Laminates", desc: "Premium decorative finishes." },
  { title: "Wooden Flooring", desc: "Engineered & solid wood floors." },
  { title: "Natural Timber", desc: "Teak, Sheesham, Eucalyptus." },
];

const WHY_US = [
  { icon: Hammer, title: "In-House Manufacturing", desc: "State-of-the-art unit ensuring precision, consistency and timelines." },
  { icon: Layers, title: "Material Expertise", desc: "25+ years of working with the finest timber and engineered boards." },
  { icon: Sparkles, title: "End-to-End Execution", desc: "From concept and design to manufacturing and installation." },
];

const STATS = [
  { n: "25+", l: "Years of legacy" },
  { n: "100+", l: "Projects delivered" },
  { n: "7", l: "Door categories" },
];

export default function Home() {
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "General", message: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data || [])).catch(() => {});
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/inquiries", form);
      toast.success("Thank you! Our team will get back to you shortly.");
      setForm({ name: "", email: "", phone: "", service: "General", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F4] text-[#2B2623]">
      <Navbar />

      {/* HERO */}
      <section id="home" className="relative min-h-screen flex items-end pb-24 pt-32 overflow-hidden grain">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Modular kitchen" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#2B2623]/85 via-[#2B2623]/55 to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.4em] text-[#E5E0D5] mb-6 reveal">
              Raw to Remarkable · Est. Since 25 Years
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-7xl text-[#F9F8F4] leading-[1.05] tracking-tight reveal reveal-delay-1">
              Precision-crafted<br />interiors rooted in<br />
              <em className="text-[#E5C9A8]">material excellence.</em>
            </h1>
            <p className="mt-7 text-base md:text-lg text-[#E5E0D5] max-w-xl leading-relaxed reveal reveal-delay-2">
              Bolt Modular by JJ Lumbers crafts modular kitchens, wardrobes, doors
              and turnkey interiors that balance design, durability and innovation.
            </p>
            <div className="mt-10 flex flex-wrap gap-4 reveal reveal-delay-3">
              <a href="#contact" data-testid="hero-cta-quote" className="inline-flex items-center gap-2 bg-[#F9F8F4] text-[#2B2623] px-7 py-4 text-sm tracking-wide hover:bg-[#8C5931] hover:text-[#F9F8F4] transition-colors rounded-full">
                Start your project <ArrowRight size={16} />
              </a>
              <a href="#projects" data-testid="hero-cta-projects" className="inline-flex items-center gap-2 border border-[#F9F8F4]/40 text-[#F9F8F4] px-7 py-4 text-sm tracking-wide hover:border-[#F9F8F4] rounded-full">
                View projects
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* STATS STRIP */}
      <section className="border-y border-[#E5E0D5] bg-[#F3F1EA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-2 md:grid-cols-3">
          {STATS.map((s, i) => (
            <div key={i} className="py-10 md:py-14 px-4 text-center border-r last:border-r-0 border-[#E5E0D5]" data-testid={`stat-${i}`}>
              <div className="font-serif text-4xl md:text-5xl text-[#8C5931]">{s.n}</div>
              <div className="mt-2 text-xs uppercase tracking-[0.2em] text-[#6E6862]">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ABOUT / LEGACY */}
      <section id="about" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-6 relative">
            <img src={LEGACY_IMG} alt="JJ Lumbers timber yard" className="w-full h-[560px] object-cover" />
            <div className="absolute -bottom-6 -right-6 bg-[#8C5931] text-[#F9F8F4] px-6 py-5 max-w-[220px] hidden md:block">
              <div className="font-serif text-3xl leading-none">25+</div>
              <div className="text-xs uppercase tracking-[0.2em] mt-2">years of timber mastery</div>
            </div>
          </div>
          <div className="md:col-span-6">
            <p className="text-xs uppercase tracking-[0.3em] text-[#8C5931] mb-5">The Legacy</p>
            <h2 className="font-serif text-3xl md:text-5xl tracking-tight leading-[1.1]">
              A quarter century of mastering wood —<br/><em className="text-[#8C5931]">now reimagined</em> for modern living.
            </h2>
            <p className="mt-7 text-[#6E6862] leading-relaxed max-w-xl">
              JJ Lumbers built its name on the art of working with natural timber
              and engineered wood. Bolt Modular is the natural evolution — a
              precision manufacturing arm dedicated to interiors that blend
              design, durability and innovation under one roof.
            </p>
            <ul className="mt-8 space-y-3 text-sm">
              {["In-house state-of-the-art manufacturing unit","Authorised OEM cum Distributor — Greenpanel","100+ projects across residential & commercial","Single-partner accountability from raw to remarkable"].map((p,i)=>(
                <li key={i} className="flex items-start gap-3 text-[#2B2623]"><Check size={18} className="text-[#8C5931] mt-0.5"/>{p}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section id="services" className="py-24 md:py-32 bg-[#F3F1EA] border-y border-[#E5E0D5]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 mb-14">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-[#8C5931] mb-4">What we craft</p>
              <h2 className="font-serif text-3xl md:text-5xl tracking-tight">Beautiful spaces. Better living.</h2>
            </div>
            <p className="text-[#6E6862] max-w-md text-sm leading-relaxed">
              Delivering precision-crafted solutions across every aspect of modern
              interiors — one partner, one process, your perfect space.
            </p>
          </div>

          <div className="grid grid-cols-12 gap-5">
            {SERVICES.map((s, i) => {
              const span = i === 0 ? "md:col-span-8" : i === 1 ? "md:col-span-4" : i === 2 ? "md:col-span-4" : i === 3 ? "md:col-span-4" : "md:col-span-4";
              return (
                <article key={i} data-testid={`service-card-${i}`} className={`col-span-12 ${span} group relative overflow-hidden bg-[#FFFFFF] border border-[#E5E0D5]`}>
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={s.img} alt={s.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="p-6 md:p-7">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-11 h-11 rounded-full bg-[#F3F1EA] border border-[#E5E0D5] flex items-center justify-center shrink-0">
                        <s.icon size={22} className="text-[#8C5931]"/>
                      </div>
                      <h3 className="font-serif text-xl md:text-2xl">{s.title}</h3>
                    </div>
                    <p className="text-sm text-[#6E6862] leading-relaxed">{s.desc}</p>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* DOORS */}
      <section id="doors" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <p className="text-xs uppercase tracking-[0.3em] text-[#8C5931] mb-5">Doors & CNC Panels</p>
            <h2 className="font-serif text-3xl md:text-5xl leading-tight tracking-tight">
              Designed to inspire.<br/>Engineered to <em className="text-[#8C5931]">endure.</em>
            </h2>
            <p className="mt-6 text-[#6E6862] leading-relaxed max-w-md">
              From subtle veneer panels to grand 10-foot French doors — every door
              is CNC-precision crafted and finished to last a lifetime.
            </p>
            <div className="mt-9 grid grid-cols-2 gap-3">
              {DOOR_TYPES.map((d,i)=>(
                <div key={i} className="border border-[#E5E0D5] px-4 py-3 text-sm bg-[#F9F8F4] hover:bg-[#8C5931] hover:text-[#F9F8F4] transition-colors duration-300" data-testid={`door-type-${i}`}>
                  {d}
                </div>
              ))}
            </div>
          </div>
          <div className="md:col-span-7">
            <div className="relative w-full h-full min-h-[560px] bg-[#F3F1EA] overflow-hidden border border-[#E5E0D5]">
              <img src={DOOR_FEATURE} alt="Bolt Modular door collection" className="w-full h-full object-cover object-center"/>
            </div>
          </div>
        </div>
      </section>

      {/* PROJECTS */}
      <section id="projects" className="py-24 md:py-32 bg-[#2B2623] text-[#F9F8F4]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-14 gap-4">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-[#E5C9A8] mb-4">Our Work Speaks</p>
              <h2 className="font-serif text-3xl md:text-5xl tracking-tight">Selected projects.</h2>
            </div>
            <p className="text-[#C9BFA9] max-w-sm text-sm leading-relaxed">100+ delivered across residential & commercial spaces.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {(projects.length ? projects : [1,2,3,4,5,6].map(()=>({}))).map((p, i) => (
              <article key={p.id || i} data-testid={`project-card-${i}`} className="group relative overflow-hidden rounded-sm">
                <div className="aspect-[4/5] overflow-hidden bg-[#3a3530]">
                  <img
                    src={p.image_url || `https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1200&q=85&auto=format&fit=crop&sig=${i}`}
                    alt={p.title || "Project"}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-[#1a1613]/95 via-[#2B2623]/25 to-transparent"/>
                <div className="absolute bottom-0 left-0 right-0 p-6 translate-y-1 group-hover:translate-y-0 transition-transform duration-500">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[#E5C9A8] mb-2">{p.category || "Project"}</div>
                  <h3 className="font-serif text-2xl leading-tight text-[#F9F8F4]">{p.title || "Coming soon"}</h3>
                  {p.location && <p className="text-xs text-[#C9BFA9] mt-1 tracking-wide">{p.location}</p>}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* MATERIALS / GREENPANEL */}
      <section id="materials" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-12 items-center mb-16">
            <div className="md:col-span-5 order-2 md:order-1">
              <div className="relative overflow-hidden border border-[#E5E0D5]">
                <img src={MATERIALS_HERO} alt="MDF, plywood and veneer boards" className="w-full h-full object-cover"/>
                <div className="absolute bottom-4 left-4 bg-[#F9F8F4]/95 backdrop-blur px-4 py-2 border border-[#E5E0D5]">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[#8C5931]">Authorised OEM</div>
                  <div className="font-serif text-lg text-[#2B2623]">Greenpanel</div>
                </div>
              </div>
            </div>
            <div className="md:col-span-7 order-1 md:order-2">
              <p className="text-xs uppercase tracking-[0.3em] text-[#8C5931] mb-4">Materials that define excellence</p>
              <h2 className="font-serif text-3xl md:text-5xl tracking-tight leading-tight">
                Engineered boards, laminates and <em className="text-[#8C5931]">natural timber.</em>
              </h2>
              <p className="mt-6 text-[#6E6862] leading-relaxed max-w-xl">
                As Authorised OEM cum Distributor of Greenpanel Industries, every
                surface we craft begins with sourced-right MDF, HDHMR, plywood,
                laminates and premium natural timber — the foundation of finishes
                that endure.
              </p>
              <div className="mt-8 grid grid-cols-2 gap-3">
                {["MDF","HDHMR","Plywood","Laminates","Wooden Flooring","Natural Timber"].map((m,i)=>(
                  <div key={i} data-testid={`material-tag-${i}`} className="border-l-2 border-[#8C5931] pl-4 py-1 text-sm text-[#2B2623]">{m}</div>
                ))}
              </div>
            </div>
          </div>

          {/* Marquee */}
          <div className="overflow-hidden border-y border-[#E5E0D5] py-6 mb-16">
            <div className="flex gap-16 marquee-track whitespace-nowrap">
              {[...MATERIALS, ...MATERIALS].map((m, i) => (
                <div key={i} className="flex items-center gap-3 text-2xl md:text-3xl font-serif text-[#2B2623]/30">
                  <span>{m.title}</span>
                  <span className="text-[#8C5931]">·</span>
                </div>
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {MATERIALS.map((m, i) => (
              <div key={i} data-testid={`material-${i}`} className="border border-[#E5E0D5] bg-[#F3F1EA] p-7 hover:bg-[#8C5931] hover:text-[#F9F8F4] transition-colors duration-500 group">
                <div className="font-serif text-2xl mb-2">{m.title}</div>
                <p className="text-sm text-[#6E6862] group-hover:text-[#E5E0D5]">{m.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="py-24 md:py-32 bg-[#F3F1EA] border-y border-[#E5E0D5]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-14">
            <p className="text-xs uppercase tracking-[0.3em] text-[#8C5931] mb-4">Why Bolt Modular</p>
            <h2 className="font-serif text-3xl md:text-5xl tracking-tight max-w-3xl">Designed better. Built stronger. Made to last.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {WHY_US.map((w, i) => (
              <div key={i} data-testid={`why-card-${i}`} className="bg-[#F9F8F4] border border-[#E5E0D5] p-8">
                <w.icon size={28} className="text-[#8C5931] mb-5"/>
                <h3 className="font-serif text-2xl mb-3">{w.title}</h3>
                <p className="text-sm text-[#6E6862] leading-relaxed">{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <p className="text-xs uppercase tracking-[0.3em] text-[#8C5931] mb-5">Let's collaborate</p>
            <h2 className="font-serif text-3xl md:text-5xl tracking-tight leading-tight">
              Let's build something <em className="text-[#8C5931]">extraordinary.</em>
            </h2>
            <p className="mt-7 text-[#6E6862] leading-relaxed">
              Tell us about your space — residential or commercial — and our team
              will design a precision-crafted solution that lasts.
            </p>

            <div className="mt-10 space-y-6 text-sm">
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-full bg-[#F3F1EA] flex items-center justify-center border border-[#E5E0D5] shrink-0"><HomeIcon size={18} className="text-[#8C5931]"/></div>
                <div className="leading-relaxed">
                  <div className="font-medium text-[#2B2623]">Studio & Manufacturing</div>
                  <div className="text-[#6E6862]">Plot No. 1, Near Bakhra Bridge, Main Kachwa Road, Pundrak, Karnal, Haryana</div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-full bg-[#F3F1EA] flex items-center justify-center border border-[#E5E0D5] shrink-0"><Award size={18} className="text-[#8C5931]"/></div>
                <div className="leading-relaxed">
                  <div className="font-medium text-[#2B2623]">Talk to us</div>
                  <div className="text-[#6E6862]">+91 90848 88887 · +91 94163 04900</div>
                  <div className="text-[#6E6862]">jjlumbers07@gmail.com</div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-full bg-[#F3F1EA] flex items-center justify-center border border-[#E5E0D5] shrink-0"><Building2 size={18} className="text-[#8C5931]"/></div>
                <div className="leading-relaxed">
                  <div className="font-medium text-[#2B2623]">Sectors</div>
                  <div className="text-[#6E6862]">Residential · Commercial · Hospitality</div>
                </div>
              </div>
            </div>
          </div>

          <div className="md:col-span-7">
            <form onSubmit={onSubmit} data-testid="contact-form" className="bg-[#F3F1EA] border border-[#E5E0D5] p-8 md:p-10 space-y-5">
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-[#6E6862]">Name *</label>
                  <input data-testid="contact-name" required value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} className="mt-2 w-full bg-transparent border-b border-[#8C5931]/30 py-3 outline-none focus:border-[#8C5931] text-[#2B2623]" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-[#6E6862]">Phone *</label>
                  <input data-testid="contact-phone" required value={form.phone} onChange={(e)=>setForm({...form, phone:e.target.value})} className="mt-2 w-full bg-transparent border-b border-[#8C5931]/30 py-3 outline-none focus:border-[#8C5931]" />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-[#6E6862]">Email *</label>
                  <input type="email" data-testid="contact-email" required value={form.email} onChange={(e)=>setForm({...form, email:e.target.value})} className="mt-2 w-full bg-transparent border-b border-[#8C5931]/30 py-3 outline-none focus:border-[#8C5931]" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-[#6E6862]">Service of interest</label>
                  <select data-testid="contact-service" value={form.service} onChange={(e)=>setForm({...form, service:e.target.value})} className="mt-2 w-full bg-transparent border-b border-[#8C5931]/30 py-3 outline-none focus:border-[#8C5931]">
                    <option>General</option>
                    <option>Modular Kitchens</option>
                    <option>Wardrobes & Storage</option>
                    <option>Doors & CNC Panels</option>
                    <option>Furniture & Turnkey</option>
                    <option>Materials Supply</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-[#6E6862]">Tell us about your project *</label>
                <textarea data-testid="contact-message" required rows={5} value={form.message} onChange={(e)=>setForm({...form, message:e.target.value})} className="mt-2 w-full bg-transparent border-b border-[#8C5931]/30 py-3 outline-none focus:border-[#8C5931] resize-none" />
              </div>
              <button data-testid="contact-submit" type="submit" disabled={submitting} className="inline-flex items-center gap-2 bg-[#2B2623] text-[#F9F8F4] px-8 py-4 rounded-full hover:bg-[#8C5931] transition-colors disabled:opacity-60">
                {submitting ? "Sending..." : "Send Inquiry"} <ArrowUpRight size={16}/>
              </button>
            </form>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingActions />
    </div>
  );
}
